import {
  Accordion,
  AnchorTextLink,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Icon,
  Input,
  Modal,
  Shelf,
  Stack,
  Text,
  Select,
} from '@chaser/components';
import { Form, Formik } from 'formik';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import CollectorService from '../../services/collector-service';
import FieldWithError from '../FieldWithError/index';
import LoadBoundary from '../LoadBoundary/index';
import LoadingMessage from '../LoadingMessage/index';
import PricingTable from './PricingTable/index';
import SuggestedInvoicesList from './SuggestedInvoicesList/index';
import { ellipsisTooltip, constructCaseIdReturnUrl } from '../../util/index';
import pluralize from 'pluralize';
import style from './CollectorModal.module.scss';
import { useCacheActions } from '../../hooks/useFetch/index';
import useFetch from '../../hooks/useFetch';
import usePush from '../../hooks/usePush';
import LoadingPlaceholders from '../LoadingPlaceholders';
import countries from './countries';
import useOrganisation from '../../hooks/useOrganisation';
import { usStates } from './states';
import useCollectorRateSync from './useCollectorRateSync';

function validate(values) {
  const errors = {};
  if (!values.countryCode) errors.countryCode = 'Country code is required';
  if (!values.debtorType) errors.debtorType = 'Customer type is required';
  if (!values.address) errors.address = 'Address is required';
  if (!values.city) errors.city = 'City is required';
  if (!values.zipCode) errors.zipCode = 'Zip code is required';
  if (values.countryCode === 'US' && !values.state) errors.state = 'State is required';
  if (values.notes && values.notes.length > 250)
    errors.notes = 'Notes must be 250 characters or fewer';
  if (!values.agreeChecked) errors.agreeChecked = 'You must agree to the terms';
  if (!values.fcaChecked) errors.fcaChecked = 'You must confirm FCA compliance';
  if (!values.debituraConsentChecked)
    errors.debituraConsentChecked = 'You must consent to sharing data with Debitura';
  return errors;
}

function useCollectionsData(shortCode, contactId) {
  const { data: collectionsDebtor } = useFetch(
    CollectorService.getCollectionsDebtor,
    { contactId, shortCode },
    {
      refetchOnMount: true,
      throwOnError: false,
    },
  );

  return {
    collectionsDebtor,
  };
}

// eslint-disable-next-line react/prop-types
const CollectorModalContent = ({
  open,
  close,
  invoices = [],
  currency,
  contact,
  countryCode: countryCodeProp,
  collectionsDebtor,
  shortCode,
}) => {
  const history = useHistory();
  const countryCode = countryCodeProp || 'GB';
  const { ContactID: contactId } = contact || {};

  const savedDebtorType = collectionsDebtor?.debtorType || '';
  const savedAddress = collectionsDebtor?.address || {};
  const savedCountryCode = savedAddress?.country || countryCode;

  const [collectingInvoices, setCollectingInvoices] = useState(invoices);
  const [suggestedInvoices, setSuggestedInvoices] = useState([]);
  const [redirecting, setRedirecting] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [accordionKey, setAccordionKey] = useState(0);
  const { invalidateDomain } = useCacheActions();

  const { execute: beginCollector, loading: lLoading } = usePush(CollectorService.beginCollector, {
    errorToast: true,
    successToast: `Moved ${pluralize('invoice', collectingInvoices.length, true)} to 'In Progress'`,
  });

  const { execute: setDebtorData, loading: dLoading } = usePush(
    CollectorService.setCollectionsDebtorData,
    {
      errorToast: true,
      throwOnError: false,
    },
  );

  const { execute: getAdditionalInvoices, loading: aLoading } = usePush(
    CollectorService.getSuggestedInvoices,
    {
      throwOnError: false,
    },
  );

  const loading = lLoading || dLoading;

  async function setSuggested() {
    const [err, res] = await getAdditionalInvoices({
      shortCode,
      contactId,
      selectedInvoices: invoices,
      currencyCode: currency,
    });

    if (err) return;
    setSuggestedInvoices(res.sort((a, b) => b.DueDate - a.DueDate).slice(0, 5));
  }

  async function handleChangeCountry(countryCode, setFieldValue) {
    setFieldValue('countryCode', countryCode);
    if (countryCode === 'US') {
      setFieldValue('state', usStates[0]?.code || '');
      return;
    }

    setFieldValue('state', '');
  }

  useEffect(() => {
    if (open && !loading) {
      setCollectingInvoices(invoices);
      setSuggested();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading]);

  if (!collectingInvoices) return null;

  return (
    <Formik
      initialValues={{
        countryCode: savedCountryCode,
        address: savedAddress?.address || '',
        city: savedAddress?.city || '',
        zipCode: savedAddress?.zipCode || '',
        state: savedAddress?.state || '',
        debtorType: savedDebtorType,
        notes:
          collectionsDebtor?.collectionDetails?.notes || contact?.collectionDetails?.notes || '',
        agreeChecked: false,
        fcaChecked: false,
        debituraConsentChecked: false,
      }}
      enableReinitialize
      validateOnMount
      validate={validate}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={async (
        { countryCode, address, zipCode, state, city, notes, debtorType },
        actions,
      ) => {
        setAccordionKey(k => k + 1);
        setIsFormSubmitting(true);
        const debtorAddress = {
          address,
          zipCode,
          city,
          state: state || null,
          country: countryCode,
        };

        await setDebtorData({
          shortCode,
          contactId,
          debtorType,
          address: debtorAddress,
        });

        const [beginErr, beginRes] = await beginCollector({
          shortCode,
          invoiceIds: collectingInvoices.map(i => i._id),
          countryCode,
          notes,
          debtorType,
          debtorAddress,
        });

        if (beginErr) {
          setIsFormSubmitting(false);
          actions.setSubmitting(false);
          return;
        }

        const redirectUrl = beginRes?.url;
        const caseId = beginRes?.caseId;

        if (typeof redirectUrl === 'string' && redirectUrl.length > 0) {
          setRedirecting(true);
          actions.setSubmitting(false);
          const url = new URL(redirectUrl);
          url.searchParams.set('returnUrl', constructCaseIdReturnUrl(caseId));
          window.location.assign(url.toString());
          return;
        }

        invalidateDomain('invoice');
        setIsFormSubmitting(false);
        actions.setSubmitting(false);
        history.push('/receivables/collections/in-progress');
        close(true);
      }}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched }) => {
        const { collectingRate, refetchCollectorRate, pLoading } = useCollectorRateSync({
          open,
          values,
          collectingInvoices,
          shortCode,
        });

        const submitting = loading || isSubmitting;

        const hasCustomerDetailsErrors =
          (touched.debtorType && errors.debtorType) ||
          (touched.address && errors.address) ||
          (touched.city && errors.city) ||
          (touched.zipCode && errors.zipCode) ||
          (touched.state && errors.state);

        const hasCollectionDetailsErrors = touched.notes && errors.notes;
        const hasRequiredCustomerDetails =
          Boolean(values.debtorType) &&
          Boolean(values.address) &&
          Boolean(values.city) &&
          Boolean(values.zipCode) &&
          (values.countryCode !== 'US' || Boolean(values.state));

        return (
          <>
            <Modal.Body>
              <Box p="medium" style={{ position: 'relative' }}>
                {(redirecting || isFormSubmitting) && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255, 255, 255, 0.85)',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <LoadingMessage size={24} />
                    <Text color="Mid Neutral 1">
                      {redirecting ? 'Redirecting to provider…' : 'Submitting…'}
                    </Text>
                  </div>
                )}
                <Form>
                  <Stack
                    gap="medium"
                    data-testid="collection-modal"
                    data-trackingid="collection-modal"
                  >
                    <Text color="Dark Neutral 1">
                      Our considerate collections operate on a <strong>no-win, no-fee basis</strong>
                    </Text>
                    <Stack gap="small">
                      <Shelf gap="xxsmall" alignItems="center">
                        <Text fontSize="medium" fontWeight="bold">
                          Country
                        </Text>
                        <Text fontSize="small" color="Red">
                          *
                        </Text>
                      </Shelf>
                      <Text fontSize="small" color="Mid Neutral 1">
                        Select the country that your customer is located in
                      </Text>
                      <FieldWithError
                        disabled={submitting || pLoading}
                        onChange={e => handleChangeCountry(e.target.value, setFieldValue)}
                        name="countryCode"
                        as={Select}
                        width={350}
                      >
                        <option value="" disabled>
                          Select a country
                        </option>
                        {countries.map(c => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </FieldWithError>
                    </Stack>
                    <hr style={{ margin: 0, border: 'none', borderTop: '1px solid #e5e7eb' }} />
                    <Accordion key={accordionKey} allowMultiple defaultOpenIndex={[0]}>
                      <Accordion.Item
                        header={({ open }) => (
                          <Shelf justifyContent="space-between" style={{ width: '100%' }}>
                            <Shelf gap="xxsmall" alignItems="center">
                              <Text fontSize="medium" fontWeight="bold">
                                Customer details
                              </Text>
                              <Text fontSize="small" color="Red">
                                *
                              </Text>
                            </Shelf>
                            <Shelf gap="xsmall" alignItems="center">
                              {hasCustomerDetailsErrors && <Icon name="info" color="Red" />}
                              <Button
                                className={style.editButton}
                                appearance="secondary"
                                size="narrow"
                              >
                                {open ? 'Hide' : 'Show'}
                              </Button>
                            </Shelf>
                          </Shelf>
                        )}
                        content={
                          <Stack gap="xsmall">
                            <FieldWithError
                              disabled={submitting || pLoading}
                              name="debtorType"
                              as={Select}
                              width="95%"
                            >
                              <option value="" disabled>
                                Customer type *
                              </option>
                              <option value="Private">Person</option>
                              <option value="Company">Company</option>
                            </FieldWithError>
                            <FieldWithError
                              disabled={submitting || pLoading}
                              name="address"
                              as={Input}
                              placeholder="Customer address *"
                              width="95%"
                            />
                            <FieldWithError
                              disabled={submitting || pLoading}
                              name="city"
                              as={Input}
                              placeholder="City *"
                              width="95%"
                            />
                            <Shelf
                              gap="small"
                              alignItems="flex-start"
                              flexWrap="nowrap"
                              width="full"
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {values.countryCode === 'US' ? (
                                  <FieldWithError
                                    disabled={submitting || pLoading}
                                    name="state"
                                    as={Select}
                                    width="100%"
                                  >
                                    <option value="" disabled>
                                      Select a state *
                                    </option>
                                    {usStates.map(s => (
                                      <option key={s.code} value={s.code}>
                                        {s.name}
                                      </option>
                                    ))}
                                  </FieldWithError>
                                ) : (
                                  <FieldWithError
                                    disabled={submitting || pLoading}
                                    name="state"
                                    as={Input}
                                    placeholder="State (optional)"
                                    width="100%"
                                  />
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <FieldWithError
                                  disabled={submitting || pLoading}
                                  name="zipCode"
                                  as={Input}
                                  placeholder="Post code / Zip code *"
                                  width="90%"
                                />
                              </div>
                            </Shelf>
                          </Stack>
                        }
                      />
                      <Accordion.Item
                        header={({ open }) => (
                          <Shelf justifyContent="space-between" style={{ width: '100%' }}>
                            <Text fontSize="medium" fontWeight="bold">
                              Collection details
                            </Text>
                            <Shelf gap="xsmall" alignItems="center">
                              {hasCollectionDetailsErrors && <Icon name="info" color="Red" />}
                              <Button
                                className={style.editButton}
                                appearance="secondary"
                                size="narrow"
                              >
                                {open ? 'Hide' : 'Show'}
                              </Button>
                            </Shelf>
                          </Shelf>
                        )}
                        content={
                          <Stack gap="small">
                            <Text fontSize="small" color="Mid Neutral 1">
                              Add any additional information relevant to this collection
                            </Text>
                            <Stack gap="xxsmall">
                              <FieldWithError
                                disabled={submitting}
                                name="notes"
                                as={Input}
                                multiline
                                type="textarea"
                                placeholder="Notes"
                                width="95%"
                                maxLength={250}
                              />
                              <Text fontSize="xsmall" color="Mid Neutral 1" trim={false}>
                                {250 - (values.notes?.length ?? 0)} characters remaining
                              </Text>
                            </Stack>
                          </Stack>
                        }
                      />
                      <Accordion.Item
                        header={({ open }) => (
                          <Shelf justifyContent="space-between" style={{ width: '100%' }}>
                            <Stack gap="small" textAlign="left">
                              <Text fontSize="medium" fontWeight="bold">
                                Invoices ({collectingInvoices.length})
                              </Text>
                            </Stack>
                            <Button
                              className={style.editButton}
                              appearance="secondary"
                              size="narrow"
                            >
                              {open ? 'Hide' : 'Show'}
                            </Button>
                          </Shelf>
                        )}
                        content={
                          <div className={style.rateSection}>
                            <Stack gap="small">
                              <PricingTable
                                invoices={collectingInvoices}
                                rate={collectingRate}
                                countryCode={values.countryCode}
                                currency={currency}
                                isRateReady={hasRequiredCustomerDetails}
                              />
                              <LoadingPlaceholders loading={aLoading} rows={1}>
                                <SuggestedInvoicesList
                                  loading={pLoading}
                                  disabled={submitting}
                                  suggestedInvoices={suggestedInvoices}
                                  addInvoice={async invoice => {
                                    const updatedCollectingInvoices = [
                                      ...collectingInvoices,
                                      invoice,
                                    ];
                                    const newSuggestedList = suggestedInvoices.filter(
                                      ({ _id: iId }) => iId !== invoice._id,
                                    );
                                    setSuggestedInvoices(newSuggestedList);
                                    setCollectingInvoices(updatedCollectingInvoices);
                                    await refetchCollectorRate(updatedCollectingInvoices);
                                  }}
                                />
                              </LoadingPlaceholders>
                            </Stack>
                            {pLoading && (
                              <div className={style.rateLoadingOverlay}>
                                <LoadingMessage size={20} />
                              </div>
                            )}
                          </div>
                        }
                      />
                    </Accordion>
                    <Stack gap="medium">
                      <Text color="Mid Neutral 1">
                        All communications will be handled within the Debitura platform, including
                        updates, requests of further details and additional documents upload
                      </Text>
                      <Text>
                        Want to discuss this quote?{' '}
                        <AnchorTextLink
                          target="_blank"
                          href="https://meetings.hubspot.com/manfredi-pellitteri/request-info-debts-collection"
                        >
                          Book in a call
                        </AnchorTextLink>{' '}
                        with our Customer Success team
                      </Text>
                      <Stack gap="xsmall">
                        <FieldWithError
                          data-testid="fca-checkbox"
                          data-trackingid="fca-checkbox"
                          name="fcaChecked"
                          as={Checkbox}
                          type="checkbox"
                          width="min-content"
                          disabled={submitting}
                        >
                          I confirm we are not lenders or hire companies who's debts might require
                          FCA authorisation to collect <span style={{ color: 'red' }}>*</span>
                        </FieldWithError>
                        <FieldWithError
                          data-testid="agree-checkbox"
                          data-trackingid="agree-checkbox"
                          name="agreeChecked"
                          as={Checkbox}
                          type="checkbox"
                          disabled={submitting}
                        >
                          I agree to Chaser Collections{' '}
                          <AnchorTextLink
                            trim={false}
                            target="_blank"
                            monochrome
                            href="https://www.chaserhq.com/collector-terms"
                          >
                            terms and conditions
                          </AnchorTextLink>{' '}
                          and the quoted fee <span style={{ color: 'red' }}>*</span>
                        </FieldWithError>
                        <FieldWithError
                          data-testid="debitura-consent-checkbox"
                          data-trackingid="debitura-consent-checkbox"
                          name="debituraConsentChecked"
                          as={Checkbox}
                          type="checkbox"
                          disabled={submitting}
                        >
                          I consent to Chaser sharing my company details and case information
                          (including debtor data) with Debitura to enable debt collection services.
                          View{' '}
                          <AnchorTextLink
                            trim={false}
                            target="_blank"
                            monochrome
                            href="https://www.debitura.com/privacy-policy"
                          >
                            Debitura's Privacy Policy
                          </AnchorTextLink>{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </FieldWithError>
                      </Stack>
                      <Text color="Mid Neutral 1">
                        The above invoices will be collected by{' '}
                        <AnchorTextLink
                          trim={false}
                          target="_blank"
                          monochrome
                          href="https://help.chaserhq.com/navigate-the-chaser-collections-page"
                        >
                          Debitura
                        </AnchorTextLink>
                      </Text>
                    </Stack>
                  </Stack>
                </Form>
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Form style={{ width: '100%' }}>
                <Shelf justifyContent="flex-end">
                  <ButtonGroup>
                    <Button appearance="secondary" onClick={close}>
                      Cancel
                    </Button>
                    <Button
                      data-testid="quote-btn"
                      data-trackingid="quote-btn"
                      disabled={submitting}
                      type="submit"
                    >
                      Request Collection
                    </Button>
                  </ButtonGroup>
                </Shelf>
              </Form>
            </Modal.Footer>
          </>
        );
      }}
    </Formik>
  );
};

// eslint-disable-next-line react/prop-types
const CollectorModal = ({ open, close, onClosed, invoices, shortCode }) => {
  const organisation = useOrganisation();
  const { CountryCode: orgCountryCode } = organisation;
  const contact = invoices.length > 0 && (invoices[0]?.Contact || invoices[0]?.contact);
  const currency = invoices.length > 0 && invoices[0]?.CurrencyCode;
  const countryCode = contact?.countryCode || orgCountryCode;
  const { collectionsDebtor } = useCollectionsData(shortCode, contact?.ContactID);

  return (
    <Modal
      bodyPadding="none"
      footerMargin="none"
      bodyClassName={style.overlay}
      size="medium"
      subtitle={<Text color="Mid Neutral 1">{ellipsisTooltip(contact?.Name, 50)}</Text>}
      open={open}
      title="Request debt collection"
      toggle={close}
    >
      <LoadBoundary
        fallback={
          <div style={{ position: 'relative', minHeight: '400px' }}>
            <LoadingMessage center />
          </div>
        }
      >
        <CollectorModalContent
          open={open}
          close={close}
          onClosed={onClosed}
          invoices={invoices}
          shortCode={shortCode}
          countryCode={countryCode}
          currency={currency}
          contact={contact}
          collectionsDebtor={collectionsDebtor}
        />
      </LoadBoundary>
    </Modal>
  );
};

export default CollectorModal;
